import { generalCurrencyFormatter } from '../../lib/helper/formatting';
import TimelineCostsIcon from './TimelineCostsIcon';
import React from 'react';

const CcsdCodeCostAndTimelineIcon = ({
  ccsdCodeCostToUse,
  consultantType,
  isDefaultCost = false,
  isOverride = false,
  pricingRuleVariant = 'icon',
  emptyLabel = '-',
}) => {
  const currentCostForType = ccsdCodeCostToUse.currentCosts.find((currentCost) => {
    return String(currentCost.consultantType?.code) === String(consultantType.code);
  });
  const bespokePrice = currentCostForType?.cost;

  // Rule-derived pricing never applies to insurer defaults, and DefaultCcsdCodeCost objects carry
  // no resolvedPrices anyway.
  const resolvedPrice = !isDefaultCost ? ccsdCodeCostToUse.resolvedPrices?.[consultantType?.code] : undefined;

  const resolvedBreakdown = !isDefaultCost ? ccsdCodeCostToUse.resolvedPriceBreakdowns?.[consultantType?.code] : undefined;

  // costMismatch is keyed by consultant type code, so it attributes to exactly one column.
  const mismatch = ccsdCodeCostToUse.costMismatch?.[consultantType?.code];
  // A pricing rule that resolves above the published default makes the mismatch warning misleading -
  // the consultant is no longer being paid less than the insurer's rate.
  const effectiveMismatch = mismatch && (resolvedPrice == null || resolvedPrice <= mismatch.defaultCost) ? mismatch : null;

  const displayCost = effectiveMismatch ? effectiveMismatch.defaultCost : (resolvedPrice ?? bespokePrice);
  const pricingRuleApplied = !effectiveMismatch && !isDefaultCost && resolvedPrice != null && resolvedPrice !== bespokePrice;
  const pricingRuleBreakdown = pricingRuleApplied ? (resolvedBreakdown ?? '') : '';

  // A rule scoped to this insurer claimed the column but could not price it, so the API sent an
  // explanation with no number (MED-987). Without this the breakdown would be dropped on the floor:
  // pricingRuleApplied requires a resolvedPrice, so the cell would render a bare "-" and say nothing
  // about the rule that was meant to fill it.
  const pricingRuleUnpriced = !effectiveMismatch && !isDefaultCost && displayCost == null && resolvedBreakdown != null;

  const isBadge = pricingRuleVariant === 'badge';
  const pricingRuleTitle = isBadge
    ? pricingRuleBreakdown
    : [
        'Rule applied',
        '---',
        pricingRuleBreakdown,
        ...(bespokePrice != null ? [`Consultant's stored rate: ${generalCurrencyFormatter(bespokePrice)}`] : []),
      ].join('\r\n');

  return (
    <>
      {displayCost != null && !isNaN(displayCost) ? (
        generalCurrencyFormatter(displayCost)
      ) : pricingRuleUnpriced ? (
        <span title={resolvedBreakdown} className="text-muted font-italic">
          n/a
        </span>
      ) : (
        emptyLabel
      )}
      {isOverride && (
        <span title="Consultant specific fee-assured override">
          <i className="pl-1 text-danger fa fa-asterisk fa-xs" />
        </span>
      )}
      {effectiveMismatch && (
        <span title={`Showing insurer's default rate. Consultant's rate: ${generalCurrencyFormatter(effectiveMismatch.cost)}`}>
          <i className="pl-1 text-warning fa fa-info-circle" />
        </span>
      )}
      {pricingRuleApplied &&
        (isBadge ? (
          <span title={pricingRuleTitle} className="badge badge-info ml-1" style={{ fontSize: '0.65em', verticalAlign: 'middle', cursor: 'default' }}>
            Rule applied
          </span>
        ) : (
          <span title={pricingRuleTitle}>
            <i className="pl-1 text-info fa fa-info-circle" />
          </span>
        ))}
      {!(pricingRuleApplied && isBadge) && (
        <TimelineCostsIcon ccsdCodeCost={ccsdCodeCostToUse} consultantType={consultantType} isDefaultCost={isDefaultCost} isOverride={isOverride} />
      )}
    </>
  );
};

export default CcsdCodeCostAndTimelineIcon;
